import { Loader2 } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { toast } from 'sonner';

import { useCancelGHLInvoiceMutation } from '~entities/parent/api/cancel-ghl-invoice';
import { useCancelInvoiceMutation } from '~entities/student/api/payments/cancel-invoice';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';

interface CancelInvoiceProps {
  invoiceId: string;
  isGHL: boolean;
}

export const CancelInvoice = forwardRef<HTMLButtonElement, CancelInvoiceProps>(
  ({ invoiceId, isGHL }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const {
      mutateAsync: cancelGHLInvoice,
      isPending: isCancelGHLInvoicePending,
    } = useCancelGHLInvoiceMutation();

    const { mutateAsync: cancelInvoice, isPending: isCancelInvoicePending } =
      useCancelInvoiceMutation();

    const handleCancelInvoice = async () => {
      if (isGHL) {
        try {
          await cancelGHLInvoice({ invoiceId });
          toast.success('Invoice discarded successfully');
          setIsOpen(false);
        } catch (error) {
          console.error(error);
          toast.error('Failed to discard invoice');
        }
      } else {
        try {
          await cancelInvoice({ invoiceId: [invoiceId] });
          toast.success('Invoice cancelled successfully');
          setIsOpen(false);
        } catch (error) {
          console.error(error);
          toast.error('Failed to cancel invoice');
        }
      }
    };

    return (
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogTrigger asChild>
          <Button variant='outline' ref={ref}>
            Discard Invoice From Portal
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Invoice From Portal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to discard this invoice from the portal?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isCancelGHLInvoicePending || isCancelInvoicePending}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleCancelInvoice}
              disabled={isCancelGHLInvoicePending || isCancelInvoicePending}
            >
              {(isCancelGHLInvoicePending || isCancelInvoicePending) && (
                <Loader2 className='size-4 animate-spin' />
              )}
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);

CancelInvoice.displayName = 'CancelInvoice';
